import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import MapboxGlobe from '../../components/MapboxGlobe/MapboxGlobe';
import {
  useBunkerRouteApi,
  BunkerRouteResponse,
  BunkerRow,
  AvailabilityEntry,
  AvailabilityStatus,
  DEFAULT_AVAILABILITY_STATUSES,
} from '../../api/ApiBunkerRoute/ApiBunkerRoute';
import './BunkerRoute.scss';

// The deep-link landing page behind the VMS Bunker Desk widgets/webhooks.
// Renders a stored snapshot only — when the snapshot's TTL has passed the API
// answers 410 and this page tells the user to reopen the link from their VMS.

const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso; // date-only price stamps
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const fmtMonth = (yyyyMm: string): string => {
  const d = new Date(`${yyyyMm}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return yyyyMm;
  return d.toLocaleString('en', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const fmtRate = (value: number | null | undefined): string =>
  value == null ? '-' : value.toLocaleString('en', { maximumFractionDigits: 2 });

// First forward month AFTER the current month — forwards only exist from next
// month on (spot covers the current month), same clamp as the VoyCalc app's
// Bunkering Options card. Stored curves can still hold the current/past months,
// so filter rather than trust curve order. Fallback: the corner-case flat value
// (formula over spot-only sources).
const forwardRate = (row: BunkerRow): string => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
  const upcoming = (row.forward.curve || [])
    .filter((p) => p.date >= nextKey)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (upcoming.length > 0) {
    return `${fmtRate(upcoming[0].value)} (${fmtMonth(upcoming[0].date)})`;
  }
  return fmtRate(row.forward.flatValue);
};

const AvailabilityBadge: React.FC<{
  entry?: AvailabilityEntry | null;
  statuses: AvailabilityStatus[];
}> = ({ entry, statuses }) => {
  // Unset level = the first (default) status — mirrors the desk UI.
  const level = entry?.level || statuses[0]?.value;
  if (!level) return <span className="bk-muted">-</span>;
  const status = statuses.find((s) => s.value === level);
  if (!status) return <span className="bk-badge bk-badge--yellow">{level}</span>;
  return <span className={`bk-badge bk-badge--${status.color}`}>{status.label}</span>;
};

const BunkerRoute: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const src = query.get('src');

  const { fetchBunkerRoute } = useBunkerRouteApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [data, setData] = useState<BunkerRouteResponse | null>(null);
  const [openCurve, setOpenCurve] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setExpired(false);

      const result = await fetchBunkerRoute(id, src);

      if (result.ok && result.data) {
        setData(result.data);
      } else if (result.expired) {
        setExpired(true);
      } else {
        setError(result.message || 'Failed to load bunker data');
      }

      setLoading(false);
    };

    loadData();
  }, [id, src, fetchBunkerRoute]);

  const statuses = useMemo<AvailabilityStatus[]>(
    () =>
      data?.availabilityStatuses && data.availabilityStatuses.length > 0
        ? data.availabilityStatuses
        : DEFAULT_AVAILABILITY_STATUSES,
    [data]
  );

  // "Last updated" across all identified rows: the most recent of the rows'
  // spot.asOf / updatedAt / fetch.lastChangeAt (same rule as the VMS card).
  const lastUpdated = useMemo<string | null>(() => {
    if (!data) return null;
    const stamps = data.ports
      .flatMap((p) => p.bunkers)
      .flatMap((b) => [b.spot.asOf, b.updatedAt, b.fetch?.lastChangeAt])
      .filter((s): s is string => !!s)
      .sort();
    return stamps.length > 0 ? stamps[stamps.length - 1] : null;
  }, [data]);

  const portMarkers = useMemo(
    () =>
      (data?.ports || []).map((p) => ({
        lat: p.lat,
        lon: p.lon,
        name: p.portName || p.portNo,
        deviationNm: p.deviationNm,
      })),
    [data]
  );

  // The forward curve opens in a modal rather than inline — with many ports
  // and bunker types the table can run to dozens of rows, so an in-table or
  // below-table panel gets pushed far from the row that opened it and reads
  // as disconnected. A modal also sidesteps the horizontal table scroll: an
  // in-table row breaks the port rowSpans and sticky doesn't work inside
  // table cells in Chromium.
  const openRow = useMemo(() => {
    if (!data || !openCurve) return null;
    const sep = openCurve.indexOf('|');
    const portNo = openCurve.slice(0, sep);
    const typeId = openCurve.slice(sep + 1);
    const port = data.ports.find((p) => p.portNo === portNo);
    const row = port?.bunkers.find((b) => b.bunkerTypeId === typeId);
    return port && row && row.forward.curve.length > 0 ? { port, row } : null;
  }, [data, openCurve]);

  const srcLabel = src === 'dataloy' ? 'Dataloy' : src === 'imos' ? 'Veson IMOS' : null;
  const routeDesc =
    typeof data?.voyage_meta?.routeDesc === 'string' ? data.voyage_meta.routeDesc : null;

  if (!id) {
    return (
      <div className="bunker-route-container">
        <div className="bk-message bk-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>No bunker overview ID provided. Please open the link from your VMS.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bunker-route-container">
        <div className="bk-message bk-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading bunker overview...</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="bunker-route-container">
        <div className="bk-message bk-expired">
          <i className="bi bi-hourglass-bottom"></i>
          <h2>This bunker overview has expired</h2>
          <p>
            Price snapshots are only kept for a limited time.
            <br />
            Please open the link again from your VMS to get a fresh overview.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bunker-route-container">
        <div className="bk-message bk-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bunker-route-container">
      <div className="bk-header">
        <h1>Bunkering Options</h1>
        {routeDesc && <p className="bk-route-desc">{routeDesc}</p>}
        {data && (
          <p className="bk-asof">
            <i className="bi bi-clock-history"></i> Prices as of {fmtDateTime(data.asOf)}
            {srcLabel && <span className="bk-src"> · snapshot from {srcLabel}</span>}
          </p>
        )}
      </div>

      {data && (
        <>
          <section className="bk-top-section">
            <div className="bk-summary-panel">
              <h2>Summary</h2>
              <table className="bk-table bk-summary-table">
                <tbody>
                  <tr>
                    <td>Identified ports</td>
                    <td className="bk-value">{data.ports.length}</td>
                  </tr>
                  <tr>
                    <td>Last updated</td>
                    <td className="bk-value">{fmtDateTime(lastUpdated)}</td>
                  </tr>
                  <tr>
                    <td>Search radius</td>
                    <td className="bk-value">{data.radiusNm} nm</td>
                  </tr>
                </tbody>
              </table>
              <div className="bk-snapshot-note">
                <i className="bi bi-info-circle"></i>
                <span>
                  This is a stored snapshot — figures do not update live. Reopen the link
                  from your VMS for current prices.
                </span>
              </div>
            </div>

            <div className="bk-globe-panel">
              <MapboxGlobe
                waypoints={data.wp}
                ports={portMarkers}
                corridorNm={data.radiusNm}
                height="400px"
              />
            </div>
          </section>

          <section className="bk-section">
            <h2>Bunker options along the route</h2>
            <div className="table-responsive">
              <table className="bk-table bk-prices-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="bk-col-port">Port</th>
                    <th rowSpan={2}>Deviation</th>
                    <th rowSpan={2}>Type</th>
                    <th colSpan={4} className="bk-group">Spot</th>
                    <th colSpan={5} className="bk-group">Forward</th>
                  </tr>
                  <tr>
                    <th>Avail</th>
                    <th>Rate</th>
                    <th>Updated</th>
                    <th>Comment</th>
                    <th>Avail</th>
                    <th>Rate</th>
                    <th>Updated</th>
                    <th>Comment</th>
                    <th>Curve</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ports.flatMap((port) => {
                    const rows = port.bunkers.length > 0 ? port.bunkers : [null];
                    return rows.map((row, idx) => {
                      const key = row
                        ? `${port.portNo}|${row.bunkerTypeId}`
                        : `${port.portNo}|none`;
                      return (
                        <tr key={key}>
                          {idx === 0 && (
                            <>
                              <td rowSpan={rows.length} className="bk-port-cell">
                                {port.portName || port.portNo}
                              </td>
                              <td rowSpan={rows.length}>
                                {Math.round(port.deviationNm)} nm
                              </td>
                            </>
                          )}
                          {row ? (
                            <>
                              <td className="bk-type-cell">
                                {row.bunkerTypeName || row.bunkerTypeId}
                              </td>
                              <td>
                                <AvailabilityBadge
                                  entry={row.availability?.spot}
                                  statuses={statuses}
                                />
                              </td>
                              <td className="bk-value">{fmtRate(row.spot.value)}</td>
                              <td>{fmtDateTime(row.spot.asOf)}</td>
                              <td className="bk-comment">
                                {row.availability?.spot?.comment || '-'}
                              </td>
                              <td>
                                <AvailabilityBadge
                                  entry={row.availability?.forward}
                                  statuses={statuses}
                                />
                              </td>
                              <td className="bk-value">{forwardRate(row)}</td>
                              <td>
                                {fmtDateTime(row.fetch?.lastChangeAt || row.updatedAt)}
                              </td>
                              <td className="bk-comment">
                                {row.availability?.forward?.comment || '-'}
                              </td>
                              <td>
                                {row.forward.curve && row.forward.curve.length > 0 ? (
                                  <button
                                    type="button"
                                    className="bk-curve-toggle"
                                    onClick={() => setOpenCurve(key)}
                                  >
                                    <i className="bi bi-graph-up"></i>{' '}
                                    {row.forward.curve.length} mo
                                  </button>
                                ) : (
                                  <span className="bk-muted">-</span>
                                )}
                              </td>
                            </>
                          ) : (
                            <td colSpan={10} className="bk-muted">
                              No bunker types managed for this port
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })}
                  {data.ports.length === 0 && (
                    <tr>
                      <td colSpan={12} className="bk-muted">
                        No managed bunker ports within {data.radiusNm} nm of this route.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {openRow && createPortal(
              <div className="modal-overlay" onClick={() => setOpenCurve(null)}>
                <div
                  className="modal-content bk-curve-modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="modal-close"
                    onClick={() => setOpenCurve(null)}
                  >
                    &times;
                  </button>
                  <div className="bk-curve-panel-title">
                    Forward curve — {openRow.port.portName || openRow.port.portNo}
                    {' · '}
                    {openRow.row.bunkerTypeName || openRow.row.bunkerTypeId}
                  </div>
                  <div className="bk-curve-grid">
                    {[...openRow.row.forward.curve]
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((p) => (
                        <div key={p.date} className="bk-curve-point">
                          <span className="bk-curve-month">{fmtMonth(p.date)}</span>
                          <span className="bk-curve-value">{fmtRate(p.value)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>,
              document.body
            )}
            <p className="bk-table-note">
              Rates in USD/MT. Forward rate shows the nearest month — open the curve
              for all months.
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default BunkerRoute;
